import { useLayoutEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Button,
  EmptyState,
  FormField,
  Heading,
  Input,
  Label,
  SelectField,
  Stepper,
  Text,
  type StepperStep,
} from "@lendcord/concord";
import { useSubmitApplicationMutation } from "./api/onboarding.api";
import { useListBranchesQuery } from "./api/branches.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { isoToWireDate } from "@/app/dateFormat";
import { AADHAAR_REGEX, isAtLeast18, PAN_REGEX, PINCODE_REGEX } from "@/app/profile/validators";
import { EMAIL_REGEX, PHONE_REGEX, toIndianE164 } from "@/auth/validators";
import { getRoleFromToken } from "@/app/tokenStorage";
import type { ApplicationListItem, ApplicationType, DocumentRef } from "./onboarding.types";
import { INDIAN_STATES } from "./indiaStates";
import { DocumentUploadSection } from "./DocumentUploadSection";
import { ApplicationsList } from "./ApplicationsList";
import { ApplicationDetailView } from "./ApplicationDetailView";
import { ApplicationReviewFlow } from "./review/ApplicationReviewFlow";
import { getJourneySteps } from "./review/applicationJourney";
import { isFieldAllowed, isFieldRequired } from "./onboardingFieldPolicy";

// Mirrors lendcord-core's OnboardingService allowed-targets matrix (ADMIN's
// own ENTERPRISE target is omitted — onboarding an ENTERPRISE isn't part of
// this form's scope). Falls back to the original BORROWER/LENDER-only set for
// an unrecognized/missing role rather than showing nothing.
const APPLICATION_TYPE_OPTIONS_BY_ROLE: Record<string, ApplicationType[]> = {
  ADMIN: ["BORROWER", "LENDER", "STAFF", "AGENT"],
  ENTERPRISE: ["STAFF", "AGENT", "BORROWER", "LENDER"],
  STAFF: ["AGENT"],
  AGENT: ["BORROWER", "LENDER"],
};

function getSelectableApplicationTypes(): ApplicationType[] {
  const role = getRoleFromToken();
  return (role && APPLICATION_TYPE_OPTIONS_BY_ROLE[role]) || ["BORROWER", "LENDER"];
}

const APPLICATION_TYPE_LABEL_IDS: Record<ApplicationType, string> = {
  BORROWER: "lender.onboarding.applicationTypeBorrower",
  LENDER: "lender.onboarding.applicationTypeLender",
  STAFF: "lender.onboarding.applicationTypeStaff",
  AGENT: "lender.onboarding.applicationTypeAgent",
};

interface FormState {
  applicationType: ApplicationType | "";
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  pan: string;
  aadhaar: string;
  dob: string;
  addressLine1: string;
  addressDistrict: string;
  addressState: string;
  addressPincode: string;
  loanAmount: string;
  branchId: string;
  latitude: string;
  longitude: string;
}

const EMPTY_FORM: FormState = {
  applicationType: "",
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  pan: "",
  aadhaar: "",
  dob: "",
  addressLine1: "",
  addressDistrict: "",
  addressState: "",
  addressPincode: "",
  loanAmount: "",
  branchId: "",
  latitude: "",
  longitude: "",
};

type PageView = "list" | "form" | "detail" | "review";

export function OnboardingPage() {
  const intl = useIntl();
  const [submitApplication] = useSubmitApplicationMutation();
  const [view, setView] = useState<PageView>("list");
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState<{
    applicationId: string;
    readableId: string;
    generatedPassword: string;
  } | null>(null);

  const [submittingApp, setSubmittingApp] = useState(false);
  const [documents, setDocuments] = useState<DocumentRef[]>([]);
  const { data: branches, isLoading: isLoadingBranches } = useListBranchesQuery();
  const hasNoBranches = !isLoadingBranches && (branches?.length ?? 0) === 0;

  // District/state become required together the moment an address line is
  // entered — mirrors KycDetails' same pattern.
  const isAddressStarted = Boolean(form.addressLine1.trim());
  // STAFF/AGENT are the only applicationTypes with BRANCH_ID in the field
  // policy's REQUIRED set — see onboardingFieldPolicy.ts, mirrors
  // lendcord-core's OnboardingFieldPolicy exactly.
  const isTeamMember = isFieldRequired(form.applicationType, "BRANCH_ID");
  // STAFF/AGENT don't carry LOCATION in the policy's ALLOWED set at all —
  // sending it for them would be rejected server-side (ONBOARDING_FIELD_NOT_ALLOWED).
  const isLocationAllowed = isFieldAllowed(form.applicationType, "LOCATION");

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const panInputRef = useRef<HTMLInputElement>(null);
  const pendingPanSelectionRef = useRef<number | null>(null);

  // .toUpperCase() rewrites the DOM's own value whenever a typed character's
  // case changes, and browsers reset the caret to the end on any
  // programmatic value write — restoring the pre-transform caret position is
  // the standard fix (case-only transform never changes string length, so
  // the same index is still correct after the rewrite). The restore has to
  // happen in useLayoutEffect (synchronously after the DOM commit, before
  // paint) — a requestAnimationFrame callback fires after paint, which loses
  // the race against the next keystroke during fast typing.
  function handlePanChange(event: ChangeEvent<HTMLInputElement>) {
    pendingPanSelectionRef.current = event.target.selectionStart;
    updateField("pan", event.target.value.toUpperCase());
  }

  useLayoutEffect(() => {
    if (pendingPanSelectionRef.current !== null) {
      panInputRef.current?.setSelectionRange(pendingPanSelectionRef.current, pendingPanSelectionRef.current);
      pendingPanSelectionRef.current = null;
    }
  }, [form.pan]);

  function handleBackToList() {
    setForm(EMPTY_FORM);
    setDocuments([]);
    setErrors({});
    setFormError(undefined);
    setSubmitted(null);
    setSelectedApplicationId(null);
    setView("list");
  }

  function handleAddNew() {
    handleBackToList();
    setView("form");
  }

  function handleViewApplication(application: ApplicationListItem) {
    setSelectedApplicationId(application.id);
    setView("detail");
  }

  function handleEditApplication(application: ApplicationListItem) {
    setSelectedApplicationId(application.id);
    setView("review");
  }

  function handleExitToReview() {
    if (!submitted) return;
    setSelectedApplicationId(submitted.applicationId);
    setView("review");
  }

  function handleFormSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);
    setSubmitted(null);

    const nextErrors: typeof errors = {};
    if (!form.applicationType) {
      nextErrors.applicationType = intl.formatMessage({ id: "lender.onboarding.applicationTypeRequiredError" });
    }
    if (!form.firstName.trim()) {
      nextErrors.firstName = intl.formatMessage({ id: "lender.onboarding.firstNameRequiredError" });
    }
    if (!EMAIL_REGEX.test(form.email)) {
      nextErrors.email = intl.formatMessage({ id: "lender.onboarding.emailFormatError" });
    }
    if (!PHONE_REGEX.test(form.phone)) {
      nextErrors.phone = intl.formatMessage({ id: "lender.onboarding.phoneFormatError" });
    }
    if (form.pan && !PAN_REGEX.test(form.pan)) {
      nextErrors.pan = intl.formatMessage({ id: "lender.profile.panFormatError" });
    }
    if (isFieldRequired(form.applicationType, "AADHAAR") && !form.aadhaar) {
      nextErrors.aadhaar = intl.formatMessage({ id: "lender.onboarding.aadhaarRequiredError" });
    } else if (form.aadhaar && !AADHAAR_REGEX.test(form.aadhaar)) {
      nextErrors.aadhaar = intl.formatMessage({ id: "lender.profile.aadhaarFormatError" });
    }
    if (isFieldRequired(form.applicationType, "DOB") && !form.dob) {
      nextErrors.dob = intl.formatMessage({ id: "lender.onboarding.dobRequiredError" });
    } else if (form.dob && new Date(form.dob) >= new Date()) {
      nextErrors.dob = intl.formatMessage({ id: "lender.profile.dobFutureError" });
    } else if (form.dob && !isAtLeast18(form.dob)) {
      nextErrors.dob = intl.formatMessage({ id: "lender.profile.dobMinAgeError" });
    }
    if (isFieldRequired(form.applicationType, "ADDRESSES") && !form.addressLine1.trim()) {
      nextErrors.addressLine1 = intl.formatMessage({ id: "lender.onboarding.addressLine1RequiredError" });
    }
    if (form.addressLine1 && !form.addressDistrict.trim()) {
      nextErrors.addressDistrict = intl.formatMessage({ id: "lender.onboarding.addressDistrictRequiredError" });
    }
    if (form.addressLine1 && !form.addressState) {
      nextErrors.addressState = intl.formatMessage({ id: "lender.profile.addressStateRequiredError" });
    }
    if (form.addressPincode && !PINCODE_REGEX.test(form.addressPincode)) {
      nextErrors.addressPincode = intl.formatMessage({ id: "lender.profile.addressPincodeFormatError" });
    }
    if (isFieldRequired(form.applicationType, "LOAN_AMOUNT") && (!form.loanAmount || isNaN(Number(form.loanAmount)) || Number(form.loanAmount) <= 0)) {
      nextErrors.loanAmount = intl.formatMessage({ id: "lender.onboarding.loanAmountRequiredError" });
    }
    if (isTeamMember && !form.branchId) {
      nextErrors.branchId = intl.formatMessage({ id: "lender.onboarding.branchRequiredError" });
    }
    if (isLocationAllowed) {
      if (!form.latitude || isNaN(Number(form.latitude)) || Number(form.latitude) < -90 || Number(form.latitude) > 90) {
        nextErrors.latitude = intl.formatMessage({ id: "lender.onboarding.latitudeRequiredError" });
      }
      if (!form.longitude || isNaN(Number(form.longitude)) || Number(form.longitude) < -180 || Number(form.longitude) > 180) {
        nextErrors.longitude = intl.formatMessage({ id: "lender.onboarding.longitudeRequiredError" });
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmittingApp(true);
    submitApplication({
      applicationType: form.applicationType as ApplicationType,
      firstName: form.firstName.trim(),
      ...(form.middleName.trim() ? { middleName: form.middleName.trim() } : {}),
      ...(form.lastName.trim() ? { lastName: form.lastName.trim() } : {}),
      email: form.email,
      phone: toIndianE164(form.phone),
      consentGiven: true,
      ...(form.pan ? { pan: form.pan } : {}),
      ...(form.aadhaar ? { aadhaar: form.aadhaar } : {}),
      ...(form.dob ? { dob: isoToWireDate(form.dob) } : {}),
      ...(form.addressLine1
        ? {
            addresses: [
              {
                type: "CURRENT" as const,
                line1: form.addressLine1,
                city: form.addressDistrict,
                state: form.addressState,
                pincode: form.addressPincode,
                country: "IN",
              },
            ],
          }
        : {}),
      // STAFF/AGENT don't carry a loan amount at all — lendcord-core's
      // OnboardingFieldPolicy doesn't even allow the field for them, so it
      // must be omitted entirely rather than sent as 0.
      ...(isFieldAllowed(form.applicationType, "LOAN_AMOUNT")
        ? { loanAmount: form.applicationType === "LENDER" ? 0 : Number(form.loanAmount) || 0 }
        : {}),
      // Same reasoning as LOAN_AMOUNT above — STAFF/AGENT don't have LOCATION
      // in the policy's ALLOWED set, so omit it entirely for them rather
      // than sending it and getting an ONBOARDING_FIELD_NOT_ALLOWED 400.
      ...(isLocationAllowed && (form.latitude || form.longitude)
        ? {
            location: {
              address: form.addressLine1 || "",
              latitude: Number(form.latitude) || 0,
              longitude: Number(form.longitude) || 0,
            },
          }
        : {}),
      ...(documents.length > 0 ? { documents } : {}),
      ...(form.branchId ? { branchId: form.branchId } : {}),
    })
      .unwrap()
      .then((response) => {
        setSubmitted({
          applicationId: response.application.id,
          readableId: response.readableId,
          generatedPassword: response.generatedPassword,
        });
        setForm(EMPTY_FORM);
        setDocuments([]);
      })
      .catch((error) => {
        setFormError(
          isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.onboarding.submitError" }),
        );
      })
      .finally(() => {
        setSubmittingApp(false);
      });
  }

  if (view === "list") {
    return (
      <div className="p-4 sm:p-6">
        <ApplicationsList onAddNew={handleAddNew} onView={handleViewApplication} onEdit={handleEditApplication} />
      </div>
    );
  }

  if (view === "detail" && selectedApplicationId) {
    return (
      <div className="p-4 sm:p-6">
        <ApplicationDetailView applicationId={selectedApplicationId} onBack={handleBackToList} />
      </div>
    );
  }

  if (view === "review" && selectedApplicationId) {
    return (
      <div className="p-4 sm:p-6">
        <ApplicationReviewFlow applicationId={selectedApplicationId} onBack={handleBackToList} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div>
        <div className="mb-4">
          <button
            type="button"
            onClick={handleBackToList}
            aria-label={intl.formatMessage({ id: "lender.onboarding.backToApplications" })}
            className="text-sm font-medium text-accent-600 hover:underline"
          >
            ‹ <FormattedMessage id="lender.onboarding.backToApplications" />
          </button>
        </div>
        <Heading level={2}>
          <FormattedMessage id="lender.onboarding.pageTitle" />
        </Heading>
        <Text as="p" size="sm" variant="muted" className="mt-1">
          <FormattedMessage id="lender.onboarding.pageDescription" />
        </Text>

        <Stepper
          className="mt-6"
          steps={getJourneySteps(form.applicationType || "BORROWER").map(
            (step, index): StepperStep => ({
              id: step.id,
              label: intl.formatMessage({ id: step.labelId }),
              // Step 1 (NEW_APPLICATION) is current pre-submit, complete once
              // submitted successfully — at which point the application already
              // exists on the backend in READY_FOR_REVIEW, so that step shows
              // as current here too, even though its form only mounts after Exit.
              status: submitted
                ? index === 0
                  ? "complete"
                  : index === 1
                    ? "current"
                    : "upcoming"
                : index === 0
                  ? "current"
                  : "upcoming",
            }),
          )}
        />

        {submitted && (
          <div className="mt-6 flex flex-col gap-3">
            <AlertBanner status="success">
              <FormattedMessage id="lender.onboarding.submitSuccess" />
            </AlertBanner>
            <div className="rounded-md border border-border bg-surface-raised p-4">
              <Text as="p" size="sm" className="font-medium">
                <FormattedMessage id="lender.onboarding.generatedCredentialsHeading" />
              </Text>
              <Text as="p" size="sm" className="mt-2">
                <FormattedMessage id="lender.onboarding.generatedReadableId" values={{ id: submitted.readableId }} />
              </Text>
              <Text as="p" size="sm">
                <FormattedMessage id="lender.onboarding.generatedPassword" values={{ password: submitted.generatedPassword }} />
              </Text>
            </div>
            <div>
              <Button type="button" variant="secondary" onClick={handleExitToReview}>
                <FormattedMessage id="lender.onboarding.exitButton" />
              </Button>
            </div>
          </div>
        )}

        {!submitted && (
          <form onSubmit={handleFormSubmit} noValidate className="mt-6 rounded-md border border-border bg-surface-raised">
            {formError && <AlertBanner status="error">{formError}</AlertBanner>}

            <section className="px-6 py-3">
              <Heading level={4} className="text-accent-600">
                <FormattedMessage id="lender.onboarding.applicationTypeHeading" />
              </Heading>
              <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
                <SelectField
                  label={intl.formatMessage({ id: "lender.onboarding.applicationTypeLabel" })}
                  placeholder={intl.formatMessage({ id: "lender.onboarding.applicationTypePlaceholder" })}
                  value={form.applicationType}
                  onValueChange={(value) => {
                    const nextType = value as ApplicationType;
                    updateField("applicationType", nextType);
                    if (nextType === "LENDER") updateField("loanAmount", "0");
                    if (isFieldRequired(nextType, "BRANCH_ID")) {
                      updateField("loanAmount", "");
                    } else {
                      updateField("branchId", "");
                    }
                  }}
                  error={errors.applicationType}
                  required
                  options={getSelectableApplicationTypes().map((type) => ({
                    value: type,
                    label: intl.formatMessage({ id: APPLICATION_TYPE_LABEL_IDS[type] }),
                  }))}
                />
                <FormField
                  label={intl.formatMessage({ id: "lender.onboarding.targetEmailLabel" })}
                  error={errors.email}
                  required
                >
                  <Input
                    value={form.email}
                    onChange={(event) => updateField("email", event.target.value)}
                    type="email"
                    placeholder="jane@example.com"
                  />
                </FormField>
                <FormField
                  label={intl.formatMessage({ id: "lender.onboarding.targetPhoneLabel" })}
                  error={errors.phone}
                  required
                >
                  <div className="flex">
                    <span className="flex items-center rounded-sm border border-border bg-surface px-3 text-sm text-text-muted" aria-hidden="true">+91</span>
                    <Input
                      value={form.phone}
                      onChange={(event) => updateField("phone", event.target.value.replace(/\D/g, "").slice(0, 10))}
                      inputMode="numeric"
                      className="flex-1"
                    />
                  </div>
                </FormField>
                <FormField
                  label={intl.formatMessage({ id: "lender.onboarding.firstNameLabel" })}
                  error={errors.firstName}
                  required
                >
                  <Input
                    value={form.firstName}
                    onChange={(event) => updateField("firstName", event.target.value)}
                    placeholder="Jane"
                  />
                </FormField>
                <FormField label={intl.formatMessage({ id: "lender.onboarding.middleNameLabel" })}>
                  <Input
                    value={form.middleName}
                    onChange={(event) => updateField("middleName", event.target.value)}
                    placeholder="M."
                  />
                </FormField>
                <FormField label={intl.formatMessage({ id: "lender.onboarding.lastNameLabel" })}>
                  <Input
                    value={form.lastName}
                    onChange={(event) => updateField("lastName", event.target.value)}
                    placeholder="Doe"
                  />
                </FormField>
              </div>
            </section>

            <section className="px-6 py-3">
              <Heading level={4} className="text-accent-600">
                <FormattedMessage id="lender.onboarding.personalDetailsHeading" />
              </Heading>
              <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
                <FormField label={intl.formatMessage({ id: "lender.profile.panLabel" })} error={errors.pan}>
                  <Input
                    ref={panInputRef}
                    value={form.pan}
                    onChange={handlePanChange}
                    placeholder={intl.formatMessage({ id: "lender.profile.panPlaceholder" })}
                    maxLength={10}
                  />
                </FormField>
                <FormField label={intl.formatMessage({ id: "lender.profile.aadhaarLabel" })} error={errors.aadhaar}>
                  <Input
                    value={form.aadhaar}
                    onChange={(event) => updateField("aadhaar", event.target.value.replace(/\D/g, ""))}
                    placeholder={intl.formatMessage({ id: "lender.profile.aadhaarPlaceholder" })}
                    maxLength={12}
                  />
                </FormField>
                <FormField label={intl.formatMessage({ id: "lender.profile.dateOfBirthLabel" })} error={errors.dob}>
                  <Input
                    type="date"
                    value={form.dob}
                    onChange={(event) => updateField("dob", event.target.value)}
                  />
                </FormField>
              </div>
            </section>

            <section className="px-6 py-3">
              <Heading level={4} className="text-accent-600">
                <FormattedMessage id="lender.onboarding.addressHeading" />
              </Heading>
              <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div className="sm:col-span-3">
                  <FormField
                    label={intl.formatMessage({ id: "lender.profile.addressLine1Label" })}
                    error={errors.addressLine1}
                    required={isFieldRequired(form.applicationType, "ADDRESSES")}
                  >
                    <Input
                      value={form.addressLine1}
                      onChange={(event) => updateField("addressLine1", event.target.value)}
                    />
                  </FormField>
                </div>
                <SelectField
                  label={intl.formatMessage({ id: "lender.onboarding.addressStateLabel" })}
                  placeholder={intl.formatMessage({ id: "lender.onboarding.addressStatePlaceholder" })}
                  value={form.addressState}
                  onValueChange={(value) => {
                    updateField("addressState", value);
                    updateField("addressDistrict", "");
                  }}
                  error={errors.addressState}
                  required={isAddressStarted}
                  options={Object.keys(INDIAN_STATES).map((s) => ({ value: s, label: s }))}
                />
                <SelectField
                  label={intl.formatMessage({ id: "lender.onboarding.addressDistrictLabel" })}
                  placeholder={intl.formatMessage({ id: "lender.onboarding.addressDistrictPlaceholder" })}
                  value={form.addressDistrict}
                  onValueChange={(value) => updateField("addressDistrict", value)}
                  disabled={!form.addressState}
                  error={errors.addressDistrict}
                  required={isAddressStarted}
                  options={(form.addressState ? INDIAN_STATES[form.addressState] : []).map((d) => ({
                    value: d,
                    label: d,
                  }))}
                />
                <FormField label={intl.formatMessage({ id: "lender.profile.addressPincodeLabel" })} error={errors.addressPincode}>
                  <Input
                    value={form.addressPincode}
                    onChange={(event) => updateField("addressPincode", event.target.value.replace(/\D/g, ""))}
                    maxLength={6}
                  />
                </FormField>
                {isLocationAllowed && (
                <div className="flex flex-col gap-1.5">
                  <Label>
                    <FormattedMessage id="lender.onboarding.coordinatesLabel" />
                  </Label>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      navigator.geolocation.getCurrentPosition(
                        (pos) => {
                          updateField("latitude", pos.coords.latitude.toFixed(6));
                          updateField("longitude", pos.coords.longitude.toFixed(6));
                        },
                        () => {
                          setFormError(intl.formatMessage({ id: "lender.onboarding.geoLocationError" }));
                        },
                      );
                    }}
                    className="w-full"
                  >
                    <FormattedMessage id="lender.onboarding.getCurrentLocationButton" />
                  </Button>
                </div>
                )}
                {isLocationAllowed && (
                <FormField
                  label={intl.formatMessage({ id: "lender.onboarding.latitudeLabel" })}
                  error={errors.latitude}
                  required
                >
                  <Input
                    type="number"
                    value={form.latitude}
                    onChange={(e) => updateField("latitude", e.target.value)}
                    step="any"
                    placeholder="e.g. 26.461242"
                  />
                </FormField>
                )}
                {isLocationAllowed && (
                <FormField
                  label={intl.formatMessage({ id: "lender.onboarding.longitudeLabel" })}
                  error={errors.longitude}
                  required
                >
                  <Input
                    type="number"
                    value={form.longitude}
                    onChange={(e) => updateField("longitude", e.target.value)}
                    step="any"
                    placeholder="e.g. 84.286600"
                  />
                </FormField>
                )}
              </div>
            </section>

            {isTeamMember && (
              <section className="px-6 py-3">
                <Heading level={4} className="text-accent-600">
                  <FormattedMessage id="lender.onboarding.branchHeading" />
                </Heading>
                {hasNoBranches ? (
                  <div className="mt-4">
                    <EmptyState illustration="noData" bordered>
                      <FormattedMessage id="lender.onboarding.noBranchesMessage" />
                    </EmptyState>
                  </div>
                ) : (
                  <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
                    <SelectField
                      label={intl.formatMessage({ id: "lender.onboarding.branchLabel" })}
                      placeholder={intl.formatMessage({
                        id: isLoadingBranches
                          ? "lender.onboarding.branchLoadingPlaceholder"
                          : "lender.onboarding.branchPlaceholder",
                      })}
                      value={form.branchId}
                      onValueChange={(value) => updateField("branchId", value)}
                      error={errors.branchId}
                      required
                      disabled={isLoadingBranches}
                      options={(branches ?? []).map((branch) => ({ value: branch.id, label: branch.name }))}
                    />
                  </div>
                )}
              </section>
            )}

            {isFieldRequired(form.applicationType, "LOAN_AMOUNT") && (
              <section className="px-6 py-3">
                <Heading level={4} className="text-accent-600">
                  <FormattedMessage id="lender.onboarding.loanDetailsHeading" />
                </Heading>
                <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <FormField
                    label={intl.formatMessage({ id: "lender.onboarding.loanAmountLabel" })}
                    error={errors.loanAmount}
                    required
                  >
                    <Input
                      type="number"
                      value={form.loanAmount}
                      onChange={(event) => updateField("loanAmount", event.target.value)}
                      min={0}
                      placeholder="e.g. 500000"
                    />
                  </FormField>
                </div>
              </section>
            )}

            <section className="px-6 py-3">
              <Heading level={4} className="text-accent-600">
                <FormattedMessage id="lender.onboarding.documentHeading" />
              </Heading>
              <DocumentUploadSection documents={documents} onDocumentsChange={setDocuments} />
            </section>

            <div className="flex justify-end px-6 pb-6">
              <Button type="submit" disabled={submittingApp}>
                {submittingApp ? (
                  <FormattedMessage id="lender.onboarding.submittingButton" />
                ) : (
                  <FormattedMessage id="lender.onboarding.submitButton" />
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
